import { NgModule } from "@angular/core";
import { Route, RouterModule } from "@angular/router";
import { ENVIRONMENT } from "../environments/environment";
import { Module } from "./classes/flow/base/Module";
import { RoleName } from "./classes/flow/session/Session";
import { AuthCallbackComponent } from "./components/auth-callback/auth-callback.component";
import { HasRoleGuard } from "./guards/hasRole";
import { ModuleGuard } from "./guards/Module";
import { SpecialtyGuard } from "./guards/Specialty";
import { AllQuestionnairesComponent } from "./screens/content/all-questionnaires/all-questionnaires.component";
import { QuestionnaireComponent } from "./screens/content/all-questionnaires/questionnaire/questionnaire.component";
import { CoachEnergyConsultComponent } from "./screens/content/coach/coach-energy-consult/coach-energy-consult.component";
import { CoachComponent } from "./screens/content/coach/coach.component";
import { ContentComponent } from "./screens/content/content";
import { CoordinatorComponent } from "./screens/content/coordinator/Coordinator.component";
import { ResidentComponent } from "./screens/content/resident/Resident.component";
import { DashboardStatisticsComponent } from "./screens/content/shared/dashboard-statistics/dashboard-statistics.component";
import { DashboardComponent } from "./screens/content/shared/dashboard/dashboard";
import { Error_403Component } from "./screens/error/403/403";
import { Error_404Component } from "./screens/error/404/404";
import { ErrorComponent } from "./screens/error/error";
import { LoggedOutComponent } from "./screens/guest/logged-out/logged-out.component";
import { NewEnergyConsultComponent } from "./screens/guest/new-energy-consult/new-energy-consult.component";
import { NewQuestionnaireComponent } from "./screens/new-questionnaire/new-questionnaire.component";
import { ReportComponent } from "./screens/report/report.component";
import { ViewReportComponent } from "./screens/view-report/view-report.component";

export interface RouteData {
  allowedSpecialties?: number[];
  allowedRoles?: RoleName[];
  noAuthRedirectURL?: string;
  module?: Module;
}

interface AppRoute extends Route {
  data?: RouteData;
  children?: AppRoute[];
}

const routes: AppRoute[] = [
  {
    path: "",
    pathMatch: "full",
    redirectTo: "request",
  },
  {
    path: "content",
    component: ContentComponent,

    children: [
      {
        path: "",
        pathMatch: "full",
        component: ContentComponent,
        canActivate: [HasRoleGuard],
        data: { allowedRoles: ["coordinator", "resident", "coach"] },
      },
      {
        path: "coordinator",
        component: CoordinatorComponent,
        canActivate: [HasRoleGuard],
        data: { allowedRoles: ["coordinator"] },
        children: [
          {
            path: "",
            pathMatch: "full",
            redirectTo: "dashboard",
          },
          {
            path: "dashboard",
            component: DashboardComponent,
            canActivate: [SpecialtyGuard],
            data: { allowedSpecialties: [13], noAuthRedirectURL: "/content/coordinator/dashboard-statistics" },
          },
          {
            path: "dashboard-statistics",
            component: DashboardStatisticsComponent,
            canActivate: [SpecialtyGuard, ModuleGuard],
            data: { allowedSpecialties: [12], module: "STATISTICS_DASHBOARD" },
          },
          {
            path: "dashboard/:id",
            component: DashboardComponent,
            canActivate: [SpecialtyGuard],
            data: { allowedSpecialties: [13] },
          },
          {
            path: "all-questionnaires",
            component: AllQuestionnairesComponent,
            canActivate: [SpecialtyGuard],
            data: { allowedSpecialties: [13] },
          },
          {
            path: "new-questionnaire",
            component: NewQuestionnaireComponent,
            canActivate: [SpecialtyGuard],
            data: { allowedSpecialties: [13] },
          },
          {
            path: "questionnaire/:id",
            component: QuestionnaireComponent,
            canActivate: [SpecialtyGuard],
            data: { allowedSpecialties: [13] },
          },
          {
            path: "request/:id",
            component: CoachEnergyConsultComponent,
            canActivate: [SpecialtyGuard],
            data: { allowedSpecialties: [13] },
          },
          {
            path: "**",
            redirectTo: "error/404",
          },
        ],
      },
      {
        path: "coach",
        component: CoachComponent,
        canActivate: [HasRoleGuard],
        data: { allowedRoles: ["coach"] },
        children: [
          {
            path: "",
            pathMatch: "full",
            redirectTo: "dashboard",
          },
          {
            path: "dashboard",
            component: DashboardComponent,
          },
          {
            path: "request/:id",
            component: CoachEnergyConsultComponent,
          },
          {
            path: "request/:id/report",
            component: ReportComponent,
          },
          {
            path: "**",
            redirectTo: "error/404",
          },
        ],
      },
      {
        path: "resident",
        component: ResidentComponent,
        canActivate: [HasRoleGuard],
        data: { allowedRoles: ["resident"] },
        children: [
          {
            path: "",
            pathMatch: "full",
            redirectTo: "dashboard",
          },
          {
            path: "dashboard",
            component: DashboardComponent,
          },
          {
            path: "report/:id",
            component: ViewReportComponent,
          },
          {
            path: "**",
            redirectTo: "error/404",
          },
        ],
      },
    ],
  },
  {
    path: "error",
    component: ErrorComponent,
    children: [
      {
        path: "404",
        component: Error_404Component,
      },
      {
        path: "403",
        component: Error_403Component,
      },
    ],
  },
  {
    path: "login",
    component: NewEnergyConsultComponent,
  },
  {
    path: "auth-callback",
    component: AuthCallbackComponent,
  },
  {
    path: "loggedout",
    component: LoggedOutComponent,
  },
  {
    path: "request",
    canActivate: [HasRoleGuard],
    data: {
      allowedRoles: [
        "resident",
        "guest",
        "user",
        ...(Object(ENVIRONMENT.MODULES.includes("NEW_REQUEST_COACH") ? ["coach"] : []) as RoleName[]),
        ...(Object(ENVIRONMENT.MODULES.includes("NEW_REQUEST_COORDINATOR") ? ["coordinator"] : []) as RoleName[]),
      ],
      noAuthRedirectURL: "/content",
    },
    component: NewEnergyConsultComponent,
  },
  {
    path: "request/:specialty",
    canActivate: [HasRoleGuard],
    data: {
      allowedRoles: ["resident", "guest", "user"],
      noAuthRedirectURL: "/content",
    },
    component: NewEnergyConsultComponent,
  },
  { path: "**", redirectTo: "error/404" },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
